import { createMachine, assign } from 'xstate'
import { z } from 'zod'

import { EmailCallback } from '../components/EmailInput'

export const emailSchema = z.string().email()
export const emailPayloadSchema = z.object({
  toEmail: z.string(),
  emailTemplateModel: z.object({
    naturalPowerContribution: z.string(),
    annualConsumption: z.string(),
    annualConsumptionAverage: z.string(),
    currentCost: z.string(),
    paymentFrequency: z.union([
      z.literal('Annually'),
      z.literal('Quarterly'),
      z.literal('Bimonthly'),
      z.literal('Monthly'),
      z.literal('Fortnightly'),
      z.literal('Weekly'),
    ]),
    annualCost: z.string(),
    estimatedPremium: z.string(),
    estimatedTotal: z.string(),
    co2Saved: z.string(),
    carsRemoved: z.string(),
  }),
})

export type EmailPayload = z.infer<typeof emailPayloadSchema>

export enum MessageType {
  Success = 'success',
  Error = 'error',
  Timeout = 'timeout',
  Disabled = 'disabled',
  Required = 'required',
  Invalid = 'invalid',
}

export type EmailReportContext = {
  dirty?: boolean
  emailAddress: string
  payload: EmailPayload
  message?: { type: MessageType; text: string }
  emailCallback: EmailCallback
  emailInputPlaceholder: string
  successMessage: string
  errorMessage: string
  timeoutMessage: string
  disabledMessage: string
  requiredMessage: string
  invalidMessage: string
  emailTimeout: number
}

type EmailReportEvents =
  | { type: 'Mark as dirty' }
  | { type: 'Update email address'; value: string }
  | { type: 'Validating email address' }
  | { type: 'Enable' }
  | { type: 'Disable' }
  | { type: 'Update inputs' }
  | { type: 'Submit disabled' }
  | { type: 'Submit whilst invalid' }
  | { type: 'Email results'; payload: EmailPayload }
  | { type: 'Retry' }
  | {
      type: 'done.invoke.sendingEmail'
      data: {
        status: string
        success: boolean
      }
    }

export const emailReportMachine =
  /** @xstate-layout N4IgpgJg5mDOIC5QFEC2BDAlgGwAQCc4BXbAF1gDoARTWdAI20goEkImBiZAOwaYG0ADAF1EoAA4B7WJlKZJ3MSAAeiAIwA2ACwUAHAE4A7ACZDgtQFYNB44IDMAGhABPRAFprgivrWHDG4y0LYzstLUMAXwinNCw8QlgScmpaPmYANXRsTAh0OW4oXDAMHFx0CAgE2A4hUSQQKRk5BSVVBDc1fTsKPxCtfUEwtV1hiydXdos7fT1BKcGLBY19YyiYkvjiMkoaOkYMrJy8zAKijbKKqpq1OolpWXlFeraOjW6NN8FDCws1Oy-tLpxu4LLoLBQtIINL47GDFsZjPo1iBYqUEkkdql9hAOABVcS5UhgM5xC6VOCwWpKRoPFrPdwfNQUUGDXSwjRQtThYGTLS6byGTq6UxQwzC1bRFHndHbFJ7Jg4gDKRHoqFkuAgWIVVPqNOaT1ALy0aiZ00MsMMRje+gsWh5bkFxgogn0-UCxl0-js32RqM2iVlPDSEAomWyEBJpXK5NglDYnD9BC25B1dyaj1aiACMzs0zswUR-kEUPtXOZakEIwrrt8c36vulycoQexocOEeKpOjVVY7DAXEbAZTN2p931mYQug+emGamMyzewWGpZNFGmLtBhiCdi5JgbpJlyRbCrb4cjeG7FIoirA3E1p07OA4EAUYAoJwAbpIANZv2C3+8oD9VMGjHDN6QQG1+QrEwtACPkK1tHk-kMCh5z8LkrFtHw-n3NEmwoY8DjPR8L0uK8bzvE5ClI59X3fbgv1-Ch-yogpgJHXUwLpQ11EEQI12MToAjmL4Qh5ISZmGLQEUGOdcytPD-QxQjeFbMMcnPMke0owDzw4MB8HwSR8AocRsDyAAzEzUBYgDqOAkRR3THiVEQYVUNBW1jFBD5pl8CSfAoTCgiMPwun4pSkyHZs1JPDSO3OS9Y2veyHw2DhlFgUg8jfdBLKJfAAApkAAJVKgB5UqAH1kAAWQAQRYAAZaqABUWDq5AKtxNrqqoXFSoajqKoAOQASgHA8CKIkMEq05LKF06jzxAvVwN4hBbQ0Cg3l0NlNDFcJ-B5GSZn8XQ+WNfwK0iyVE0PWLg1PTTSO0q9kCMkyOFKsBSHwZw1u4g03IQc0drnS7dytYsNB5UIIfzC7520RELCix7VOe+a3sWig2swVBIFwSQiFIH6-oBoGXJBtpwh0RFbF8ncujMO0XEQWwdH+RDoS6H5QnR+7BxU2bWEY9sFvI2MOGVVV1QAdwACxwbLcE-dtqdpWn3PnNDLSE6ZGa5RwOfaLcmQROwPnNCx9E9N4MZmuLiKOfIaKS6Xqi18cIJ+HafIGF02QMKc4bNtwpnBeSFi6E0RidmKsfU9tjnSrsveuW5QJpiczC8aExQ0YIbR+H57XZNDfJNoJdErUxE9Fl25tT92pZjb3jGz9bXLaS7UPNSxzRCMFK3ZiY3BZ7wTTg-pFnMO3G8DZuXsJFbccz-g7G74GJwrKddv24V+mLoUgQj8J+X0axtD835TGtpej2bjhdjSH2NtBjwdCwj1ljg+cQRw4T0Zt4HcZhbT-FzEJDQT8nrYlflqMA-BOJpm1hODoXRvBTBGOEa+W5ITj3cPmaO18NCCgMMEa2kRhbTSTrNPEBJcrtyqB-XuiAtD-GCiaS05gjBTBMPaLcOZlhfC6D4CKWg4HJwVIwwkxITjiDJpSJyXFc4QQ+F4FYbx-hWHIXyYwQi9ZzmsD4AwnR-hSNofhGKHA6roHwN+MosANSYHwKQQGqi0G+02t8Z0PM-i+DCGYTQK5oRriCOYFmxc7qSm4JICAcAlAPQIm-Vs8YwDOXQRBMsNoxTDARvmM0RD2ieGnn4eCVDgnSLSfFVu69PYdyyT4r+MknTfHIRQqciI+T2mCOCEwRhYRzBGLYOwNSkEQGaZ-F4ZgZgsj5OyTk3II6XRmC6N0MkHbeiFusOhTdsaSw3h3XsTBpnsLBsaHoQD+KFg5CWCOJpTQDCgluKYu4JR7JsQclOJFGk6TSh7OI5ydYIDeEyS61suTDEGOQwxZs5zgg+IsIs5CrCcLUNIsWON-kfS+vgEFe8oQ7X2mEESKLxJm3+PyRcyMPhnV2VKfZy9Dl-IzicgmRMIyk1IISiC0x+SVgMLmcFrNBgSTFGhL4+ZzBzxWDQr5ykWW-NerilKyoADGGqKR8s2rzNC1t7ChxNvOCuJLBT2D5D8OuuYFVMu+cqupbKoxe11aDGVzp9pdGtizb04qI62C8OuV524PlYpXiwCWzqyJNLUdkzawpug2n3jYFYJs+k+WCuyOZO5ZzhtZW7Bp7KqhuraPBbwxpS56K3MKe00If5wXtiPfa4zrFKufsGUtiB-hOiFd60VfqSluBDmuURlp-KSLgV282FhPJ10WVOZZhh7T2x0BslYWyvQ+iiBEIAA */
  createMachine(
    {
      tsTypes: {} as import('./emailReport.machine.typegen').Typegen0,
      schema: {
        context: {} as EmailReportContext,
        events: {} as EmailReportEvents,
        services: {} as {
          'Sending email': { data: { status: string } }
          'done.invoke.sendingEmail': { data: { status: string; success: boolean } }
        },
      },
      preserveActionOrder: true,
      predictableActionArguments: true,
      id: 'Email results',
      initial: 'Disabled',
      description: 'INITIAL_NATURALPOWER_CONTRIBUTION',
      states: {
        Disabled: {
          exit: 'clearMessage',
          initial: 'Validating email address',
          states: {
            Idle: {
              on: {
                Enable: {
                  target: '#Email results.Enabled',
                },
              },
            },
            'Validating email address': {
              always: [
                {
                  target: 'Idle',
                  cond: 'Email address is valid',
                  actions: 'setDisabledMessage',
                },
                {
                  target: 'Idle',
                },
              ],
            },
          },
          on: {
            'Update email address': {
              target: '.Validating email address',
              actions: 'setEmailAddress',
            },
            'Submit disabled': {
              target: '.Idle',
              actions: 'setDisabledMessage',
            },
          },
        },
        Enabled: {
          initial: 'Validating email address',
          states: {
            'Valid email address': {
              initial: 'Idle',
              states: {
                Idle: {
                  on: {
                    'Email results': [
                      {
                        target: 'Sending email',
                        cond: 'Payload is complete',
                        actions: 'updatePayload',
                      },
                      {},
                    ],
                  },
                },
                'Sending email': {
                  invoke: {
                    src: 'Emailing results',
                    id: 'sendingEmail',
                    onDone: [
                      {
                        target: 'Success',
                        cond: 'Email sent successfully',
                      },
                      {
                        target: 'Error',
                      },
                    ],
                    onError: [
                      {
                        target: 'Error',
                      },
                    ],
                  },
                  after: {
                    ERROR_EMAIL_TIMEOUT_DURATION: {
                      target: '#Email results.Enabled.Valid email address.Timed out',
                      actions: [],
                      internal: false,
                    },
                  },
                },
                Error: {
                  entry: 'setErrorMessage',
                  exit: 'clearMessage',
                  on: {
                    Retry: {
                      target: 'Sending email',
                    },
                  },
                },
                'Timed out': {
                  entry: 'setTimeoutMessage',
                  exit: 'clearMessage',
                  on: {
                    Retry: {
                      target: 'Sending email',
                    },
                  },
                },
                Success: {
                  entry: 'setSuccessMessage',
                  exit: 'clearMessage',
                },
              },
            },
            'Invalid email address': {
              exit: 'clearMessage',
              on: {
                'Submit whilst invalid': {
                  target: 'Validating email address',
                  actions: 'setDirty',
                },
              },
            },
            'Validating email address': {
              always: [
                {
                  target: 'Valid email address',
                  cond: 'Email address is valid',
                  actions: ['clearMessage', 'setDirty'],
                },
                {
                  target: 'Invalid email address',
                  cond: 'Input is pristine',
                },
                {
                  target: 'Invalid email address',
                  cond: 'Email address is empty',
                  actions: 'setRequiredMessage',
                },
                {
                  target: 'Invalid email address',
                  actions: 'setInvalidMessage',
                },
              ],
            },
          },
          on: {
            Disable: [
              {
                target: 'Disabled',
                cond: 'Input is dirty',
                actions: 'setDisabledMessage',
              },
              {
                target: 'Disabled',
              },
            ],
            'Update email address': {
              target: '.Validating email address',
              actions: 'setEmailAddress',
            },
            'Update inputs': {
              target: '.Validating email address',
            },
          },
        },
      },
      on: {
        'Mark as dirty': {
          cond: 'First interaction',
          actions: 'setDirty',
        },
      },
    },
    {
      actions: {
        setDirty: assign((_ctx, _evt) => ({ dirty: true })),
        setEmailAddress: assign((_ctx, { value }) => ({ emailAddress: value })),
        clearMessage: assign((_ctx, _evt) => ({ message: undefined })),
        setSuccessMessage: assign(({ successMessage }, _evt) => ({
          message: { type: MessageType.Success, text: successMessage },
        })),
        setErrorMessage: assign(({ errorMessage }, _evt) => ({
          message: { type: MessageType.Error, text: errorMessage },
        })),

        setTimeoutMessage: assign(({ timeoutMessage }, _evt) => ({
          message: { type: MessageType.Timeout, text: timeoutMessage },
        })),
        setDisabledMessage: assign(({ disabledMessage }, _evt) => ({
          message: { type: MessageType.Disabled, text: disabledMessage },
        })),
        setRequiredMessage: assign(({ requiredMessage }, _evt) => ({
          message: { type: MessageType.Required, text: requiredMessage },
        })),
        setInvalidMessage: assign(({ invalidMessage }, _evt) => ({
          message: { type: MessageType.Invalid, text: invalidMessage },
        })),
        updatePayload: assign((_ctx, { payload }) => ({ payload })),
      },
      services: {
        'Emailing results': ({ payload, emailCallback }) => emailCallback(payload),
      },
      guards: {
        'Email address is valid': ({ emailAddress }) => emailSchema.safeParse(emailAddress).success === true,
        'Email address is empty': ({ emailAddress }) => emailAddress.length === 0,
        'Input is pristine': ({ dirty }) => !dirty,
        'Input is dirty': ({ dirty }) => !!dirty,
        'Payload is complete': ({ payload }) => emailPayloadSchema.safeParse(payload).success === true,
        'First interaction': ({ dirty, emailAddress }) => !dirty && emailAddress.length > 0,
        'Email sent successfully': (_ctx, { data }) =>
          data.status?.toLocaleLowerCase().includes('success') || data.success === true,
      },
      delays: {
        ERROR_EMAIL_TIMEOUT_DURATION: ({ emailTimeout }) => emailTimeout,
      },
    },
  )
